import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Check, XCircle, Settings2 } from 'lucide-react';
import { useConsent } from '../context/ConsentContext';

/**
 * GDPR cookie consent banner.
 *
 * Behaviour:
 *   * Only renders when the user has not yet made a decision.
 *   * Three categories: Necessary (locked on), Analytics, Marketing.
 *   * Links to the Privacy Policy page.
 *   * Choice persists via localStorage (see ConsentContext).
 */
const CookieBanner = () => {
  const { decided, save, acceptAll, rejectAll } = useConsent();
  const [showManage, setShowManage] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: true });

  if (decided) return null;

  return (
    <div className="fixed bottom-3 left-3 right-3 md:left-6 md:right-6 lg:right-6 lg:left-auto lg:max-w-lg z-[60]" data-testid="cookie-banner">
      <div className="bg-[#12263A] text-white rounded-sm shadow-2xl border-t-4 border-[#F46A25] overflow-hidden">
        {!showManage ? (
          <div className="p-4 md:p-5">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-[#F46A25] rounded-sm flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-black text-sm md:text-base mb-1">Cookies on Dr Tools</h3>
                <p className="text-xs md:text-[13px] text-white/80 leading-relaxed">
                  We use cookies to power your basket & login (necessary), understand how the site is used (analytics), and show you relevant ads (marketing).
                  {' '}
                  <Link to="/pages/privacy" className="text-[#F46A25] underline hover:no-underline" data-testid="cookie-banner-privacy-link">
                    Read our Privacy Policy.
                  </Link>
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button onClick={acceptAll} data-testid="cookie-accept-all" className="flex-1 bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-xs md:text-sm py-2 px-3 rounded-sm flex items-center justify-center gap-1.5">
                <Check className="w-4 h-4" /> Accept all
              </button>
              <button onClick={rejectAll} data-testid="cookie-reject-all" className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold text-xs md:text-sm py-2 px-3 rounded-sm flex items-center justify-center gap-1.5">
                <XCircle className="w-4 h-4" /> Reject all
              </button>
              <button onClick={() => setShowManage(true)} data-testid="cookie-manage" className="sm:w-auto text-xs md:text-sm text-white/70 hover:text-white font-semibold py-2 px-3 flex items-center justify-center gap-1.5">
                <Settings2 className="w-4 h-4" /> Manage
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 md:p-5" data-testid="cookie-manage-panel">
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-black text-sm md:text-base">Manage preferences</h3>
              <button onClick={() => setShowManage(false)} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 bg-white/5 rounded-sm cursor-not-allowed">
                <input type="checkbox" checked disabled className="mt-0.5 w-4 h-4 accent-[#F46A25]" />
                <div>
                  <div className="text-sm font-bold">Necessary <span className="text-[10px] font-normal text-white/50">· always on</span></div>
                  <div className="text-[11px] text-white/60 leading-snug">Basket, checkout, login, session — the site does not work without these.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 bg-white/5 rounded-sm cursor-pointer hover:bg-white/10">
                <input type="checkbox" checked={prefs.analytics} onChange={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))} className="mt-0.5 w-4 h-4 accent-[#F46A25]" data-testid="cookie-toggle-analytics" />
                <div>
                  <div className="text-sm font-bold">Analytics</div>
                  <div className="text-[11px] text-white/60 leading-snug">Google Analytics (GTAG) — helps us understand which pages are popular.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 bg-white/5 rounded-sm cursor-pointer hover:bg-white/10">
                <input type="checkbox" checked={prefs.marketing} onChange={() => setPrefs(p => ({ ...p, marketing: !p.marketing }))} className="mt-0.5 w-4 h-4 accent-[#F46A25]" data-testid="cookie-toggle-marketing" />
                <div>
                  <div className="text-sm font-bold">Marketing</div>
                  <div className="text-[11px] text-white/60 leading-snug">Meta Pixel + retargeting cookies for showing you relevant ads.</div>
                </div>
              </label>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => save(prefs)} data-testid="cookie-save-prefs" className="flex-1 bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm py-2 rounded-sm">
                Save my choices
              </button>
              <button onClick={rejectAll} data-testid="cookie-reject-all-alt" className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold text-sm py-2 rounded-sm">
                Reject all
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
